export type LibraryContentType = 'article' | 'video';

export interface LibraryItem {
  id: string;
  type: LibraryContentType;
  title: string;
  description: string;
  thumbnailUrl: string;
  linkUrl: string;
  lastUpdated: string;
}

/** Shape of articles array in libraries.json */
export interface LibraryArticleEntry {
  id: string;
  title: string;
  description: string;
  tags?: string[];
  license?: string;
  category?: string;
  link: string;
}

/** Shape of videos array in libraries.json */
export interface LibraryVideoEntry {
  title: string;
  description: string;
  link: string;
  thumbnail?: string;
  playlistLink?: string | null;
  channelLink?: string | null;
  updated?: string;
  isVideo?: boolean;
  isPlaylist?: boolean;
  isChannel?: boolean;
}

export interface LibrariesJson {
  articles: LibraryArticleEntry[];
  videos: LibraryVideoEntry[];
}

/** Try assets first, then public/documents (absolute paths so they work from any route). */
export const LIBRARIES_JSON_URLS = [
  '/assets/documents/libraries.json',
  '/documents/libraries.json'
];

/** Extract YouTube video ID from embed or watch URL */
function getYoutubeVideoId(url: string): string | null {
  if (!url) return null;
  // embed: https://www.youtube.com/embed/VIDEO_ID or /embed/VIDEO_ID?list=...
  const embedMatch = url.match(/youtube\.com\/embed\/([^?&#]+)/);
  if (embedMatch) return embedMatch[1];
  // watch: https://www.youtube.com/watch?v=VIDEO_ID
  const watchMatch = url.match(/youtube\.com\/watch\?.*v=([^&]+)/);
  if (watchMatch) return watchMatch[1];
  // short: https://youtu.be/VIDEO_ID
  const shortMatch = url.match(/youtu\.be\/([^?&#]+)/);
  if (shortMatch) return shortMatch[1];
  return null;
}

/** Build YouTube thumbnail URL from video ID (hqdefault is reliably available) */
function youtubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

/** Convert YouTube embed URL to watch URL for opening in new tab */
function embedToWatchUrl(embedUrl: string): string {
  const match = embedUrl.match(/embed\/([^?&#]+)/);
  if (match) return `https://www.youtube.com/watch?v=${match[1]}`;
  return embedUrl;
}

/** Extract hostname from URL for favicon (e.g. https://d2l.ai/chapter/... -> d2l.ai) */
function getDomainFromUrl(url: string): string | null {
  if (!url || !url.startsWith('http')) return null;
  try {
    const u = new URL(url);
    return u.hostname || null;
  } catch {
    return null;
  }
}

/** Google favicon API: returns site favicon as thumbnail for articles (no assets needed). */
function faviconThumbnailUrl(link: string, size = 128): string {
  const domain = getDomainFromUrl(link);
  if (!domain) return '';
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size}`;
}

/** Map libraries.json response to LibraryItem[] for articles tab and video tab */
export function mapLibrariesJsonToItems(data: LibrariesJson): LibraryItem[] {
  const items: LibraryItem[] = [];
  (data.articles || []).forEach((a) => {
    const linkUrl = a.link || '#';
    items.push({
      id: a.id,
      type: 'article',
      title: a.title,
      description: a.description || '',
      thumbnailUrl: faviconThumbnailUrl(linkUrl),
      linkUrl,
      lastUpdated: ''
    });
  });
  (data.videos || []).forEach((v, i) => {
    const linkUrl = v.link?.includes('youtube.com/embed') ? embedToWatchUrl(v.link) : (v.link || '#');
    const videoId = getYoutubeVideoId(v.link || '');
    const thumbnailUrl = v.thumbnail || (videoId ? youtubeThumbnailUrl(videoId) : '');
    items.push({
      id: `video-${i}`,
      type: 'video',
      title: v.title,
      description: v.description || '',
      thumbnailUrl,
      linkUrl,
      lastUpdated: v.updated || ''
    });
  });
  return items;
}
